// =============================================================================
// navigation/useAuth.ts — the REAL auth gate (blueprint §2 [GATE], P3 AUTH group).
//
// Resolves the root navigator's branch from the persisted session:
//   'loading'    — first resolve in flight (splash covers it; gate renders null)
//   'unauthed'   — no token  → AuthStack (Login/Register/…)
//   'onboarding' — token present but signup not finished → Onboarding only
//   'authed'     — token present + onboarding done → the full app (Tabs + stack)
//
// REACTIVITY — three signals re-resolve the gate without a manual refresh:
//   1. onAuthChange(api): storeTokens() fires `true` on login/register/apple;
//      logout() fires `false`. This is the primary login/logout signal.
//   2. notifyAuthGate() (this module): the Onboarding screen calls it after
//      finish() flips setOnboardingPending(false) — there is no api-level emitter
//      for the onboarding flag, so the gate owns this nudge. (Also re-fires when
//      a screen sets onboardingPending=true so the gate flips to 'onboarding'
//      even before storeTokens races it.)
//   3. AppState foreground: re-validate the session when returning to the app.
//
// onboardingPending (api module flag) is authoritative for the signup→onboarding
// transition; once a token exists, getMe() tells us whether onboarding completed.
// We treat a token WITHOUT a confirmed-complete onboarding as still-onboarding so
// a new Apple/email user can't slip past the username step — but we NEVER trap a
// returning user: any of {onboardingCompleted, onboardingComplete, hasOnboarded,
// onboarded} truthy OR a getMe failure (network) resolves to 'authed' rather than
// looping them back into the wizard.
// =============================================================================
import { useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';
import { getToken, getMe, onAuthChange, isOnboardingPending, setOnboardingPending } from '../services/api';

export type AuthState = 'loading' | 'unauthed' | 'onboarding' | 'authed';

// --- Gate-local notifier (for onboarding finish / start) ---------------------
// The api layer emits auth changes for token writes, but NOT for the onboarding
// flag. Screens that flip it (Onboarding.finish, the auth screens on a new
// signup) call notifyAuthGate() so the gate re-resolves immediately.
type GateListener = () => void;
const gateListeners = new Set<GateListener>();
export function notifyAuthGate(): void {
  gateListeners.forEach((l) => { try { l(); } catch {} });
}

// Read the account's onboarding-completion flag across the envelope shapes the
// backend has shipped. Returns true only when we can POSITIVELY confirm it's
// done; an ambiguous/failed read is handled by the caller (fail-open for
// returning users, fail-closed only while onboardingPending is set).
async function onboardingConfirmedComplete(): Promise<boolean | null> {
  try {
    const res = await getMe();
    if (!res || (res as any).success === false) return null; // network/5xx → unknown
    const d: any = (res as any).data ?? res;
    const u = d?.user ?? d;
    if (!u || typeof u !== 'object') return null;
    const done =
      u.onboardingCompleted ?? u.onboardingComplete ?? u.hasOnboarded ?? u.onboarded;
    if (done === true) return true;
    if (done === false) return false;
    return null; // field absent → unknown
  } catch {
    return null;
  }
}

export function useAuth(): { state: AuthState } {
  const [state, setState] = useState<AuthState>('loading');
  // Guards against an out-of-order async resolve flipping the gate to a stale
  // answer (e.g. a slow getMe landing after a logout).
  const seq = useRef(0);

  useEffect(() => {
    let mounted = true;

    async function resolve() {
      const my = ++seq.current;
      const token = await getToken();
      if (!mounted || my !== seq.current) return;

      if (!token) {
        setState('unauthed');
        return;
      }

      // A token exists. If a signup is mid-flight, the onboarding flag is
      // authoritative — route to onboarding without waiting on the network.
      if (isOnboardingPending()) {
        setState('onboarding');
        return;
      }

      // Otherwise ask the server whether onboarding completed. Fail-OPEN for
      // returning users: only a definite `false` keeps them in onboarding.
      const done = await onboardingConfirmedComplete();
      if (!mounted || my !== seq.current) return;
      if (done === false) {
        setState('onboarding');
      } else {
        // Confirmed in the app → clear any stale signup flag so a later re-resolve
        // (tab nav / AppState) can't bounce this user into onboarding again (#45).
        setOnboardingPending(false);
        setState('authed');
      }
    }

    // Initial resolve.
    resolve();

    // 1) Token writes (login/register/apple) + logout.
    const unsubAuth = onAuthChange(() => { resolve(); });
    // 2) Onboarding flag flips (start/finish).
    gateListeners.add(resolve);
    // 3) Re-validate on app foreground.
    const sub = AppState.addEventListener('change', (s) => {
      if (s === 'active') resolve();
    });

    return () => {
      mounted = false;
      unsubAuth();
      gateListeners.delete(resolve);
      sub.remove();
    };
  }, []);

  return { state };
}
